import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from 'lib/utils';
import { LIFECYCLE_STEP_KEYS, type StepState } from './lifecycle';

interface LifecycleStripProps {
  steps: [StepState, StepState, StepState];
  /** The strip's right side: checks left, the live state or the run totals. */
  aside?: React.ReactNode;
}

function StepNode({ state }: { state: StepState }) {
  if (state === 'done') {
    return (
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-success-600 text-white">
        <Check className="h-[11px] w-[11px]" strokeWidth={3} />
      </span>
    );
  }
  if (state === 'current') {
    return (
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 border-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
      </span>
    );
  }
  return <span className="h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px] border-foreground/20" />;
}

/** 44px white strip under the workspace header: Build → Activate → Run, with a slot on the right. */
export function LifecycleStrip({ steps, aside }: LifecycleStripProps) {
  const { t } = useTranslation();
  return (
    <div className="flex h-11 items-center gap-3 border-b border-border bg-card px-4">
      <ol aria-label={t('workspace_lifecycle_label')} className="flex shrink-0 items-center gap-3">
        {LIFECYCLE_STEP_KEYS.map((key, i) => {
          const state = steps[i];
          return (
            <React.Fragment key={key}>
              {i > 0 && (
                <li
                  role="presentation"
                  aria-hidden
                  className={cn('h-[1.5px] w-9', steps[i - 1] === 'done' ? 'bg-success-200 dark:bg-success-900' : 'bg-border')}
                />
              )}
              <li
                className="flex items-center gap-2"
                aria-current={state === 'current' ? 'step' : undefined}
                data-state={state}
              >
                <StepNode state={state} />
                <span
                  className={cn(
                    'whitespace-nowrap text-[12.5px]',
                    state === 'done' && 'font-medium text-foreground/80',
                    state === 'current' && 'font-semibold text-foreground',
                    state === 'upcoming' && 'text-muted-foreground',
                  )}
                >
                  {t(key)}
                </span>
              </li>
            </React.Fragment>
          );
        })}
      </ol>
      {aside != null && <div className="ml-auto flex min-w-0 items-center">{aside}</div>}
    </div>
  );
}

export default LifecycleStrip;
